import Cookies from 'js-cookie';

import htmlUtils from './htmlUtils';
import i18n from './i18n';
import UserUtils from './userUtils';
import { API_QUERY_PLACEHOLDER, DEFAULT_ROW_NUMBER } from './constants';
import envConfig from '../config';

//========================================//
//=============== API URL ===============//
//========================================//
export function getGeoAutocompleteURL(): string {
    return `${envConfig.lecab_geo.url}/places/autocomplete.json?format=json&query=${API_QUERY_PLACEHOLDER}&location=48.857%2C2.349&countries=FR&limit=5&app_key=${envConfig.lecab_geo.key}&app_code=${envConfig.lecab_geo.code}`;
}

export function getListPartnersURL(): string {
    return `/list/partners?sort=id&order=desc&page=1&search[name]=${API_QUERY_PLACEHOLDER}`;
}

export function getListDriversURL(): string {
    return `/list/drivers?sort=id&order=desc&page=1&search[driver.nameOrId]=${API_QUERY_PLACEHOLDER}`;
}

export function getListRidersURL(): string {
    return `/list/riders?sort=id&order=desc&page=1&search[fullname]=${API_QUERY_PLACEHOLDER}`;
}

export function getListCompaniesURL(): string {
    return `/list/companies/all?sort=id&order=desc&page=1&search[name]=${API_QUERY_PLACEHOLDER}`;
}

export function getListCompaniesMembershipURL(companyId: number): string {
    return `/list/companies/${companyId}/memberships?sort=id&order=desc&page=1&filters[status]=confirmed&search[rider.fullname]=${API_QUERY_PLACEHOLDER}`;
}

export function getListCompanyGroupsURL(id: string): string {
    return `${envConfig.id_url}/internal/contract/${id}/groups/list?sort=id&order=desc&search[name]=${API_QUERY_PLACEHOLDER}`;
}

export function getFindUsersURL(type = 'admin'): string {
    return `/core/users/find?type=${type}&name=${API_QUERY_PLACEHOLDER}`;
}

export function getFindUsersTicketingURL(): string {
    return `/ticketing/users?sort=id:desc&name=${API_QUERY_PLACEHOLDER}`;
}

export function getFindUsersAccountManagerURL(id: boolean = false): string {
    return `${id ? envConfig.id_url + '/' : envConfig.privapi_url}internal/account-manager/search?name=${API_QUERY_PLACEHOLDER}`;
}

export function getFindEmployeeURL(): string {
    return `${envConfig.id_url}/internal/backoffice/employee/name/${API_QUERY_PLACEHOLDER}`;
}

export function getListContractsURL(): string {
    return `${envConfig.privapi_url}internal/contract/list?search[name]=${API_QUERY_PLACEHOLDER}&pagination[num_per_page]=${DEFAULT_ROW_NUMBER}&pagination[page]=1`;
}

export function getListGroupUsersURL(groupId: string): string {
    return `${envConfig.id_url}/internal/group/${groupId}/users/list?search[fullname]=${API_QUERY_PLACEHOLDER}`;
}

export function getListPartnerVehiclesURL(partnerId: string): string {
    return `${envConfig.privapi_url}api/core/partners/${partnerId}/vehicles?search[plate]=${API_QUERY_PLACEHOLDER}`;
}

/**
 * Longest body we are willing to show as-is. Past that it is a debug page or a
 * stack trace, not a sentence meant for the operator.
 */
const MAX_PLAIN_ERROR_LENGTH = 300;

/**
 * A body that is not JSON is only worth showing when it reads as a sentence. An
 * HTML error page (nginx, a Symfony profiler) starts with a tag and runs long,
 * and pasting it into the error banner tells the operator nothing.
 */
function plainMessage(text?: string | null): string {
    const trimmed = (text || '').trim();
    if (!trimmed || trimmed.startsWith('<') || trimmed.length > MAX_PLAIN_ERROR_LENGTH) {
        return '';
    }
    return trimmed;
}

//========================================//
//=============== CORE API ===============//
//========================================//

/**
 * A service to permorm http requests to SnapCar API. Usage:
   CoreAPIService.put("/partners/1", {name:"PonyExpress"}).then((response) => {
     let payload = response.payload; // JSON Payload
   }).catch((response) => {
     let payload = response.payload; // JSON Payload
     let message = response.message; // Human-readable message
   });
 */
export default class CoreAPIService {
    static token: string;
    static BASE_URL: string;
    static language: string;

    /**
     * Past this delay a request is considered lost. Without it a hung endpoint leaves
     * its promise pending for ever, and the page waiting on it spins for ever with no
     * way out. Callers that legitimately take longer (exports) pass `options.timeout`;
     * 0 disables the abort entirely.
     */
    static DEFAULT_TIMEOUT: number = 120000;

    /**
     * token: ID Token to be sent in header, if not provided, token from cookies will be used if exists
     */
    static header(token: string) {
        const headers = new Headers();
        const cookieToken = Cookies.get('token'); // JWT token stored in cookies

        if (token !== undefined) {
            headers.append('AccessToken', token);
        }
        if (cookieToken) {
            headers.append('Token', cookieToken);
        }
        headers.append('lang', CoreAPIService.language);
        return headers;
    }

    static get(endpoint, parameters?, options?, customBaseURL?) {
        const params: any = {
            method: 'GET',
        };
        const baseURL = customBaseURL ?? CoreAPIService.BASE_URL;
        const finalUrl = `${baseURL.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
        if (parameters) {
            const url = new URL(finalUrl);
            Object.keys(parameters).forEach((key) => {
                url.searchParams.append(key, parameters[key]);
            });
            return CoreAPIService.sendRequest(url.href, params, options);
        } else {
            const url = finalUrl;
            return CoreAPIService.sendRequest(url, params, options);
        }
    }

    static post(endpoint, parameters, options?, baseURL?) {
        return this.request('POST', endpoint, parameters, options, baseURL);
    }

    static put(endpoint, parameters, options?, baseURL?) {
        return this.request('PUT', endpoint, parameters, options, baseURL);
    }

    static patch(endpoint, parameters, options?, baseURL?) {
        return this.request('PATCH', endpoint, parameters, options, baseURL);
    }

    static delete(endpoint, parameters?, options?, baseURL?) {
        return this.request('DELETE', endpoint, parameters, options, baseURL);
    }

    static request(method, endpoint, parameters, options?, customBaseURL?) {
        const params: any = {
            method: method,
            body: parameters,
        };
        if (!!params.body && !(params.body instanceof FormData)) {
            params.body = JSON.stringify(params.body);
        }

        let url;
        const baseURL = customBaseURL ?? CoreAPIService.BASE_URL;
        const finalUrl = `${baseURL.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;

        if (options?.query) {
            const url_ = new URL(finalUrl);
            Object.keys(options.query).forEach((key) => {
                url_.searchParams.append(key, options.query[key]);
            });
            url = endpoint.href;
        } else {
            url = finalUrl;
        }

        return this.sendRequest(url, params, options);
    }

    static sendRequest(url, params, options?, outApi?: boolean) {
        const currentToken = UserUtils.getToken();
        CoreAPIService.token = currentToken;

        const headers = outApi ? new Headers() : this.header(currentToken);

        // Add credentials by default for privapi
        const shouldIncludeCredentials =
            (options?.credential || url.startsWith(envConfig.privapi_url)) && options?.credentials !== 'omit';
        if (shouldIncludeCredentials) {
            params.credentials = 'include';
        }

        if ([envConfig.privapi_url, envConfig.id_url].some((prefix) => url.startsWith(prefix))) {
            headers.append('Content-Type', 'application/json');
        }

        if (options?.headers) {
            Object.keys(options.headers).forEach((key) => {
                headers.append(key, options.headers[key]);
            });
        }

        params.headers = headers;

        const timeout = options?.timeout ?? CoreAPIService.DEFAULT_TIMEOUT;
        const controller = timeout > 0 && typeof AbortController !== 'undefined' ? new AbortController() : null;
        if (controller) {
            params.signal = controller.signal;
        }

        return new Promise((resolve, reject) => {
            const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;
            const settle = (callback) => (value) => {
                if (timer) {
                    clearTimeout(timer);
                }
                callback(value);
            };
            const done = settle(resolve);
            const fail = settle(reject);

            fetch(url, params)
                .then(function (response) {
                    const contentType = response.headers.get('Content-Type');
                    const contentDisposition = response.headers.get('Content-Disposition');

                    // !JSON et !html
                    if (
                        contentType &&
                        !contentType.startsWith('application/json') &&
                        !contentType.startsWith('text/html')
                    ) {
                        return response.blob().then((blob) => {
                            const ref = response.url;

                            let needExtension = false;
                            let filename = contentDisposition
                                .split(';')
                                .find((n) => n.includes('filename='))
                                .replace('filename=', '')
                                .replace(/"/g, '')
                                .trim();

                            if (!filename && ref) {
                                needExtension = true;
                                filename = ref.substring(ref.lastIndexOf('/') + 1);
                            }
                            htmlUtils.createDownloadAction(blob, contentType, decodeURI(filename), needExtension);

                            done({ payload: {} });
                            return;
                        });
                    } else {
                        return response.text().then((text) => {
                            // Some endpoints answer a bad request with a bare sentence
                            // ("At least one filter must be send" on a list without any
                            // filter) instead of the usual {error, details} envelope.
                            // JSON.parse throws on that, and the throw used to land in the
                            // catch below as a generic "unknown_error" with status 0: the
                            // reason the server took the trouble to give was dropped.
                            let json: any = null;
                            let parsed = true;
                            try {
                                json = JSON.parse(text || null);
                            } catch (e) {
                                parsed = false;
                            }

                            const envelope = parsed && json !== null && typeof json === 'object' ? json : null;
                            const sentence = envelope ? '' : plainMessage(typeof json === 'string' ? json : text);

                            // An unparsable body is a failure even on a 2xx: the caller
                            // asked for JSON and got something it cannot read.
                            if (response.ok && parsed) {
                                done({
                                    payload: json,
                                });
                            } else {
                                const res = {
                                    key: '',
                                    message: '',
                                    status: 0,
                                    payload: {},
                                    form_errors: {},
                                    date: new Date(),
                                };
                                res.key = envelope?.error;
                                if (envelope?.details != null) {
                                    if (typeof envelope.details === 'string') {
                                        res.key = envelope.error;
                                        if (envelope.details === '') {
                                            res.message = envelope.error;
                                        } else {
                                            res.message = envelope.details;
                                        }
                                    } else if (typeof envelope.details === 'object') {
                                        res.form_errors = {};
                                        for (const key in envelope.details) {
                                            if (envelope.details.hasOwnProperty(key)) {
                                                const detail = envelope.details[key];
                                                const form_error = {
                                                    key: detail?.error || '',
                                                    message:
                                                        typeof detail === 'string'
                                                            ? detail
                                                            : detail?.description || detail?.error || key,
                                                };
                                                res.message ||= form_error.message;
                                                res.form_errors[key] = form_error;
                                            }
                                        }
                                    }
                                }
                                res.key ||= 'unknown_error';
                                res.message ||= sentence || 'Unknown error';
                                // The error reducer only ever sees `payload`, so a sentence
                                // that never was an envelope has to be dressed as one.
                                res.payload = envelope ?? (sentence ? { error_description: sentence } : {});
                                res.status = response.status;
                                fail(res);
                            }
                        });
                    }
                })
                .catch((e) => {
                    // An abort is our own timeout firing, not a bug worth a stack trace.
                    const timedOut = e?.name === 'AbortError';
                    if (!timedOut) {
                        console.trace(e);
                    }
                    const key = timedOut ? 'request_timeout' : 'unknown_error';
                    const message = i18n.t(`common:errors.${key}`, { seconds: Math.round(timeout / 1000) });
                    const res = {
                        key,
                        message,
                        status: 0,
                        // The error reducer only ever sees `payload`, so the reason has to travel in it.
                        payload: { error: key, error_description: message },
                        date: new Date(),
                    };
                    fail(res);
                });
        });
    }
}

CoreAPIService.token = '';
CoreAPIService.BASE_URL = envConfig.api_url;
const language = navigator.languages ? navigator.languages[0] : navigator.language || (navigator as any).userLanguage;
if (language) {
    CoreAPIService.language = language.substring(0, 2);
} else {
    CoreAPIService.language = 'en';
}
